import { useFormContext } from "react-hook-form";
import type { BidPostData } from "validations";

import {
  ControlledWrapper,
  InputWrapper,
} from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import type { Currency } from "@krea/shared/types/common";

import type { IRevolvingCreditBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";
import {
  AdministrationFeeTooltip,
  administrationFeeLabelText,
} from "../../bidHelpers";
import { WithdrawalInput } from "./WithdrawalInput";

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const RevolvingCreditForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: IFormProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<BidPostData<IRevolvingCreditBidInput>>();
  // The exactly-one rule reports on withdrawalFeeAmount when both are set.
  const bidErrors = errors.bids?.[bidFormIndex];
  const withdrawalError =
    bidErrors?.withdrawalFee ?? bidErrors?.withdrawalFeeAmount;

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={
            isEditing
              ? "Update revolving credit terms"
              : "Revolving credit terms"
          }
          className="mb-4"
        >
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              required
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.monthlyAmortisation`}
              label="Amortisation (per month)"
              required
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
              label={
                isInterestRatePerYearActivated
                  ? "Interest (per year)"
                  : "Interest (per month)"
              }
              required
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="" className="mb-4">
          <div className="w-full space-y-4">
            <span className="mb-2 ml-3 block text-sm italic text-gray-600">
              Here you can explain other costs & qualitative information about
              your offer
            </span>

            <InputWrapper
              label="Withdrawal fee"
              errors={withdrawalError ? [withdrawalError] : undefined}
            >
              <WithdrawalInput
                control={control}
                bidFormIndex={bidFormIndex}
                currency={currency}
              />
            </InputWrapper>

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              render={({ field }) => (
                <MoneyInput {...field} currency={currency} id={field.name} />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.limitFeePerYear`}
              label="Limit fee (per year)"
              render={({ field }) => (
                <PercentageInput
                  {...field}
                  id={field.name}
                  placeholder='Value in "%"'
                />
              )}
            />

            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.administrationFees`}
              render={({ field }) => (
                <>
                  <div className="flex items-center gap-2">
                    <FieldLabel>{administrationFeeLabelText}</FieldLabel>
                    <AdministrationFeeTooltip />
                  </div>
                  <MoneyInput {...field} currency={currency} id={field.name} />
                </>
              )}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
